import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Pagination,
  Tooltip
} from '@mui/material';
import palette from 'src/theme/palette';
import { TableNoData } from 'src/components/NoData';
import InviteModal from './InviteModal';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { LoadingButton } from '@mui/lab';
import SubProjectDialog from './SubProjectDialog';
import ProjectUserList from './ProjectUserList';
import AddEditSubProjectModal from '../SubProjectComponents/AddEditSubProjectModal';
import LoadingButtonWrapper from 'src/components/LoadingButtonWrapper';
import ProjectDetailModal from './ProjectDetailModal';
import DeleteModal from 'src/components/DeleteModal';
import { api } from 'src/Api';
import { toast } from 'react-toastify';

const ProjectTable = ({ data, loading, handlePageChange, pagination, refresh, permission }) => {
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [editingData, setEditingData] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const handleEditClick = (row) => {
    setEditingData(row);
    setEditModalOpen(true);
  };

  const handleUserClick = (project_id) => {
    setSelectedProjectId(project_id);
    setShowUserModal(true);
  };

  const handleShowDetailClick = (project_id) => {
    setSelectedProjectId(project_id);
    setShowDetailModal(true);
  };

  const handleUserClickCliose = () => {
    setSelectedProjectId(null);
    setShowUserModal(false);
  };

  const handleDeleteModal = (id) => {
    setSelectedProject(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedProject(null);
    setShowDeleteModal(false);
  };

  function handleDeleteSubProject() {
    setStatusLoading(true);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const queryParams = new URLSearchParams();
    queryParams.append('id', selectedProject);

    const requestOptions = {
      method: 'DELETE',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(`${api.delete_project}?${queryParams}`, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          handleCloseDeleteModal();
          toast.success(result.message);
          refresh();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setStatusLoading(false);
      });
  }

  const hasAnyAction =
    permission.includes('project invite') ||
    permission.includes('edit') ||
    permission.includes('add sub project') ||
    permission.includes('delete');

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '1px 2px 13px 0px rgba(0,0,0,0.2)',
          maxWidth: '100%',
          overflowX: loading ? 'hidden' : 'auto',
          position: 'relative',
          minHeight: loading && 300
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              minHeight: 250,
              background: 'rgba(255,255,255,0.7)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress size={48} />
          </Box>
        )}
        <Table stickyHeader sx={{ minWidth: 1500 }}>
          <TableHead>
            <TableRow sx={{ borderBottom: '1.5px solid #E5E7EB' }}>
              {/* Job Colour */}
              <TableCell
                sx={{
                  position: { xs: 'static', md: 'sticky' },
                  left: { xs: 'auto', md: 0 },
                  zIndex: 3,
                  fontSize: 11,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 90,
                  backgroundColor: { md: '#fff' }
                }}
              >
                Colour
              </TableCell>

              {/* Job Name */}
              <TableCell
                sx={{
                  position: { xs: 'static', md: 'sticky' },
                  left: { xs: 'auto', md: 90 },
                  zIndex: 3,
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 200,
                  backgroundColor: { md: '#fff' }
                }}
              >
                Title
              </TableCell>  

              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 200
                }}
              >
                Prefix
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 200
                }}
              >
                Sub Project
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 200
                }}
              >
                Active User
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 150
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 200
                }}
              >
                Type
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 120
                }}
              >
                Contract Price
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 180
                }}
              >
                Contract Type
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 250
                }}
              >
                Address
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 150
                }}
              >
                Project Start Date
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 220
                }}
              >
                Project Completion Date
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  minWidth: 150
                }}
              >
                Managers
              </TableCell>
              {hasAnyAction && (
                <TableCell
                  sx={{
                      fontSize: 12,
                      color: palette.primary.tableHeadTitle,
                      fontFamily: 'poppinsThin',
                      pl: 1,
                      pr: 0,
                      position: { xs: 'static', md: 'sticky' },
                      right: { xs: 'auto', md: 0 },
                      zIndex: 3,
                      backgroundColor: { md: '#fff' },
                      whiteSpace: 'nowrap'
                  }}
                >
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          {!loading && (
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id} sx={{ borderBottom: '1.5px solid #E5E7EB' }}>
                  {/* Colour */}
                  <TableCell
                    sx={{
                      position: { xs: 'static', md: 'sticky' },
                      left: { xs: 'auto', md: 0 },
                      zIndex: 2,
                      backgroundColor: '#fff',
                      minWidth: 90
                    }}
                    align="center"
                  >
                    <Box
                      sx={{
                        backgroundColor: row?.color?.code || '#000',
                        height: 25,
                        width: 25,
                        borderRadius: '50%'
                      }}
                    />
                  </TableCell>
                  {/*  Name */}
                  <TableCell
                    sx={{
                      position: { xs: 'static', md: 'sticky' },
                      left: { xs: 'auto', md: 90 },
                      zIndex: 2,
                      backgroundColor: '#fff',
                      pl: 1,
                      minWidth: 200,
                      maxWidth: 180
                    }}
                  >
                    <Tooltip title={row?.name} placement="top" arrow>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: 'poppinsRegular',
                          color: palette.primary.main,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                          maxWidth: '170px',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: palette.primary.mainDark
                          }
                        }}
                        onClick={() => handleShowDetailClick(row?.id)}
                      >
                        {row?.name}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  {/* prefix*/}
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {row?.prefix || 'N/A'}
                  </TableCell>
                  {permission.includes('view sub project') ? (
                    <TableCell
                      sx={{
                        fontSize: 12,
                        fontFamily: 'poppinsMedium',
                        pl: 1,
                        cursor:
                          row?.sub_project_count || row?.sub_project_count == 0
                            ? 'pointer'
                            : 'default',
                        color:
                          row?.sub_project_count || row?.sub_project_count == 0
                            ? palette.primary.main
                            : 'inherit',
                        '&:hover': {
                          textDecoration:
                            row?.sub_project_count || row?.sub_project_count == 0
                              ? 'underline'
                              : 'none',
                          color:
                            row?.sub_project_count || row?.sub_project_count == 0
                              ? palette.primary.mainDark
                              : 'inherit'
                        }
                      }}
                      onClick={() => {
                        if (row?.sub_project_count || row?.sub_project_count == 0) {
                          navigate('/project/sub-projects', { state: { projectData: row } });
                        }
                      }}
                    >
                      {row?.sub_project_count}
                    </TableCell>
                  ) : (
                    <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                      {row?.sub_project_count || 'N/A'}
                    </TableCell>
                  )}

                  {permission.includes('user invite') ? (
                    <TableCell
                      sx={{
                        fontSize: 12,
                        fontFamily: 'poppinsMedium',
                        pl: 1,
                        cursor: row?.user_count ? 'pointer' : 'default',
                        color: row?.user_count ? palette.primary.main : 'inherit',
                        '&:hover': {
                          textDecoration: row?.user_count ? 'underline' : 'none',
                          color: row?.user_count ? palette.primary.mainDark : 'inherit'
                        }
                      }}
                      onClick={() => row?.user_count && handleUserClick(row?.id)}
                    >
                      {row?.user_count || 'N/A'}
                    </TableCell>
                  ) : (
                    <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                      {row?.user_count || 'N/A'}
                    </TableCell>
                  )}

                  {/* status */}
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {row?.project_status || 'N/A'}
                  </TableCell>
                  {/* Type */}
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {row?.project_type_name || 'N/A'}
                  </TableCell>
                  {/* Contract Price */}
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {row?.contract_price || 'N/A'}
                  </TableCell>
                  {/* Contract type */}
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {row?.contract_type_id === 1 ? 'Fixed' : 'Open' || 'N/A'}
                  </TableCell>
                  {/* address */}
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {/* Address */}
                    {row?.city || row?.state || row?.zip_code || row?.address ? (
                      <>
                        <Tooltip title={row?.address || '-'} placement="top" arrow>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontFamily: 'poppinsMedium',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 150, // yahan apni column width ke hisaab se adjust karo
                              cursor: 'pointer'
                            }}
                          >
                            {row?.address || '-'}
                          </Typography>
                        </Tooltip>

                        {/* City, State, Zip */}
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontFamily: 'poppinsMedium'
                          }}
                        >
                          {!row?.city || (!row?.state && !row?.zip_code && !row?.address)
                            ? 'N/A'
                            : [row?.city, row?.state, row?.zip_code].filter(Boolean).join(', ')}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: 'poppinsMedium'
                        }}
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                  {/* project_start_date */}
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {row?.project_start_date || 'N/A'}
                  </TableCell>
                  {/* project_completion_date */}
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {row?.project_completion_date || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'poppinsMedium', pl: 1 }}>
                    {row?.managers && row?.managers?.length > 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px',
                          maxWidth: '120px'
                        }}
                      >
                        {row.managers.map((val, index) => (
                          <Tooltip key={index} title={val.manager_name} arrow placement="top">
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontFamily: 'poppinsRegular',
                                whiteSpace: 'nowrap', // Single line
                                overflow: 'hidden', // Extra text hide
                                textOverflow: 'ellipsis', // ...
                                cursor: 'pointer', // hover feel
                                color: palette.primary.link, // link style color
                                '&:hover': {
                                  textDecoration: 'underline',
                                  color: palette.primary.linkHover
                                } // hover effect
                              }}
                            >
                              {val.manager_name}
                            </Typography>
                          </Tooltip>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: 'poppinsMedium',
                          color: '#000'
                        }}
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                  {hasAnyAction && (
                    <TableCell
                      sx={{
                        fontSize: 12,
                        fontFamily: 'poppinsMedium',
                        pl: 1,
                        pr: 2,
                        position: { xs: 'static', md: 'sticky' },
                        right: { xs: 'auto', md: 0 },
                        zIndex: 2,
                        backgroundColor: '#fff',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {permission.includes('project invite') && (
                        <Tooltip title="Invite">
                          <span>
                            <LoadingButtonWrapper
                              variant="contained"
                              sx={{
                                width: 70,
                                height: 30,
                                fontSize: 10,
                                borderRadius: 0.7,
                                fontFamily: 'poppinsLight',
                                letterSpacing: 0.3,
                                minWidth: 10,
                                marginRight: 0.5,
                                paddingRight: 0,
                                paddingLeft: 0,
                                paddingTop: 0,
                                paddingBottom: 0,
                                transition:
                                  'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
                              }}
                              title="Invite"
                              onClick={() => handleEditClick(row)}
                              startIcon={
                                <Iconify
                                  style={{
                                    cursor: 'pointer',
                                    fontSize: 16,
                                    color: '#ffffff'
                                  }}
                                  icon="mingcute:invite-line"
                                />
                              }
                            />
                          </span>
                        </Tooltip>
                      )}

                      {permission.includes('edit') && (
                        <Tooltip title="Edit">
                          <span>
                            <LoadingButtonWrapper
                              variant="contained"
                              sx={{
                                width: 60,
                                height: 30,
                                fontSize: 10,
                                borderRadius: 0.7,
                                fontFamily: 'poppinsLight',
                                letterSpacing: 0.3,
                                minWidth: 10,
                                marginRight: 0.5,
                                paddingRight: 0,
                                paddingLeft: 0,
                                paddingTop: 0,
                                paddingBottom: 0,
                                transition:
                                  'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
                              }}
                              title="Edit"
                              component={RouterLink}
                              to="/project/edit"
                              state={{ projectData: row }}
                              startIcon={
                                <Iconify
                                  style={{
                                    cursor: 'pointer',
                                    fontSize: 15,
                                    color: '#ffffff'
                                  }}
                                  icon="cuida:edit-outline"
                                />
                              }
                            />
                          </span>
                        </Tooltip>
                      )}

                      {permission.includes('add sub project') && (
                        <Tooltip title="Sub Project">
                          <span>
                            <LoadingButtonWrapper
                              variant="contained"
                              sx={{
                                width: 90,
                                height: 30,
                                fontSize: 10,
                                borderRadius: 0.7,
                                fontFamily: 'poppinsLight',
                                letterSpacing: 0.3,
                                minWidth: 10,
                                marginRight: 0.5,
                                paddingRight: 0,
                                paddingLeft: 0,
                                paddingTop: 0,
                                paddingBottom: 0,
                                transition:
                                  'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
                              }}
                              title="Sub Project"
                              onClick={() => {
                                setShowAddEditModal(true);
                                setEditingData(row);
                              }}
                              startIcon={
                                <Iconify
                                  style={{
                                    cursor: 'pointer',
                                    fontSize: 18,
                                    color: '#ffffff',
                                    marginRight: -5
                                  }}
                                  icon="material-symbols:add-rounded"
                                />
                              }
                            />
                          </span>
                        </Tooltip>
                      )}

                      {permission.includes('delete') && (
                        <Tooltip title="Delete">
                          <LoadingButton
                            size="small"
                            variant="contained"
                            sx={{
                              justifyContent: 'center',
                              width: 30,
                              height: 30,
                              borderRadius: 0.7,
                              minWidth: 30,
                              p: 0,
                              alignSelf: 'center',
                              mr: 1,
                              backgroundColor: '#f72929ff',
                              ':hover': {
                                backgroundColor: '#cb2020ff'
                              }
                            }}
                            onClick={() => handleDeleteModal(row?.id)}
                          >
                            <Iconify
                              sx={{
                                cursor: 'pointer',
                                fontSize: 21,
                                color: '#ffffff'
                              }}
                              icon="material-symbols-light:delete-rounded"
                            />
                          </LoadingButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          )}

          {/* Empty State */}
          {data?.length === 0 && !loading && <TableNoData colSpan={16} />}
        </Table>

        {/* PAGINATION */}
        {!loading && pagination.last_page > 1 && data.length !== 0 && (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginTop: 10,
              alignItems: 'center'
            }}
            my={1}
            px={2}
          >
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </TableContainer>

      {/* INVITE MODAL */}
      <InviteModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingData(null);
        }}
        data={editingData}
        refresh={refresh}
      />

      {/* USER LIST MODAL */}
      {showUserModal && (
        <ProjectUserList
          open={showUserModal}
          onClose={handleUserClickCliose}
          projectId={selectedProjectId}
        />
      )}

      {/* DELETE SUB PROJECT MODAL */}
      <DeleteModal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteSubProject}
        loading={statusLoading}
        title="Delete Project"
        description={'Are you sure you want to delete this Project'}
      />

      {/* ADD EDIT SUB PROJECT MODAL */}
      <AddEditSubProjectModal
        open={showAddEditModal}
        onClose={() => {
          setShowAddEditModal(false);
          setEditingData(null);
        }}
        refresh={refresh}
        projectData={editingData}
      />

      {/* PROJECT DETAILS */}
      <ProjectDetailModal
        open={showDetailModal}
        onClose={() => {
          setSelectedProjectId(null);
          setShowDetailModal(false);
        }}
        projectId={selectedProjectId}
      />
    </>
  );
};

export default ProjectTable;
